import { useCallback, useEffect, useState } from 'react';

import {
  classifySageFailure,
  createSageEmployee,
  runSageEverify,
  SAGE_FAILURE,
} from '../../../../../../../datahub/sage/sageApi';

/**
 * Sage state starts from what the worker already has on record, so a reload shows the
 * standing Sage ID and E-Verify case instead of an un-onboarded worker. Action results
 * overlay that until the next user read refreshes the props.
 */
const useSageWorkerControls = ({
  selectedUserId,
  sageId,
  sageEVerify,
  dispatch,
  token,
  refreshToken,
}) => {
  const [onboardedSageId, setOnboardedSageId] = useState(sageId || null);
  const [everifyResult, setEverifyResult] = useState(sageEVerify || null);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [isRunningEverify, setIsRunningEverify] = useState(false);
  const [onboardFailure, setOnboardFailure] = useState(null);
  const [everifyFailure, setEverifyFailure] = useState(null);

  // Switching workers in the detail pane reuses this hook, so the previous worker's
  // Sage state has to be dropped rather than shown against the new one.
  useEffect(() => {
    setOnboardedSageId(sageId || null);
    setEverifyResult(sageEVerify || null);
    setOnboardFailure(null);
    setEverifyFailure(null);
  }, [selectedUserId, sageId, sageEVerify]);

  const hasSageId = Boolean(onboardedSageId);

  const onboard = useCallback(() => {
    if (!selectedUserId || isOnboarding || hasSageId) {
      return;
    }
    setIsOnboarding(true);
    setOnboardFailure(null);
    createSageEmployee({ userId: selectedUserId, dispatch, token, refreshToken })
      .then((data) => {
        setOnboardedSageId(data?.sageEmployeeId || true);
      })
      .catch((error) => {
        const failure = classifySageFailure(error);
        // Already in Sage is the state the button is trying to reach, not an error.
        if (failure.kind === SAGE_FAILURE.ALREADY_IN_SAGE) {
          setOnboardedSageId(failure.sageId || true);
          return;
        }
        setOnboardFailure(failure);
      })
      .finally(() => {
        setIsOnboarding(false);
      });
  }, [selectedUserId, isOnboarding, hasSageId, dispatch, token, refreshToken]);

  const runEverify = useCallback(() => {
    if (!selectedUserId || isRunningEverify) {
      return;
    }
    setIsRunningEverify(true);
    setEverifyFailure(null);
    runSageEverify({ userId: selectedUserId, dispatch, token, refreshToken })
      .then((data) => {
        setEverifyResult({
          status: data?.status,
          caseNumber: data?.caseNumber || null,
          // The workload stamps these in Mongo; mirror them so the card does not go
          // blank between the response and the next user read.
          eVerifier: 'sage',
          verifiedOn: Date.now(),
        });
      })
      .catch((error) => {
        const failure = classifySageFailure(error);
        if (failure.kind === SAGE_FAILURE.MISSING_SAGE_ID) {
          setOnboardedSageId(null);
        }
        setEverifyFailure(failure);
      })
      .finally(() => {
        setIsRunningEverify(false);
      });
  }, [selectedUserId, isRunningEverify, dispatch, token, refreshToken]);

  return {
    everifyFailure,
    everifyResult,
    hasSageId,
    // true is the placeholder for "onboarded, id not in the response yet".
    sageId: typeof onboardedSageId === 'string' ? onboardedSageId : null,
    isOnboarding,
    isRunningEverify,
    onboard,
    onboardFailure,
    runEverify,
  };
};

export default useSageWorkerControls;
